'use client'

import type { PricesEntry } from '@cocoonspace/sdk-js/types/pricesEntry'
import type { Space } from '@cocoonspace/sdk-js/types/space'
import type { UseQueryResult } from '@tanstack/react-query'
import { createContext, type ReactNode, useContext, useMemo } from 'react'

import { usePricesByDates } from '../use-prices-by-dates'

interface DayContextValue {
	space: Space
	value?: string
	onClickBack: () => void
	onChange: (value: string) => void
	pricesQuery: UseQueryResult<
		{
			pricesByDay: Record<string, PricesEntry>
			disabled: Date[]
		},
		Error
	>
}

const DayContext = createContext<DayContextValue | undefined>(undefined)

export const DayProvider = ({
	children,
	space,
	value,
	onChange,
	onClickBack,
}: {
	children: ReactNode
	space: Space
	value?: string
	onChange: (value: string) => void
	onClickBack: () => void
}) => {
	const pricesQuery = usePricesByDates({ space })

	const providerValue = useMemo(
		() => ({
			onChange,
			onClickBack,
			space,
			value,
		}),
		[space, value, onChange, onClickBack],
	)

	return (
		<DayContext.Provider
			value={{
				...providerValue,
				pricesQuery,
			}}
		>
			{children}
		</DayContext.Provider>
	)
}

export const useDayContext = () => {
	const context = useContext(DayContext)

	if (!context) {
		throw new Error('useDayContext must be used within a DayProvider')
	}

	return context
}
